import { Log, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { delay } from "utils/helpers";
import { clearLocalStoragePreservingSamlLoginOption } from "../samlLoginOptionCache";
import { ApplicationName, ApplicationPaths } from "./ApiAuthorizationConstants";

export class AuthorizeService {
    _callbacks = [];
    _nextSubscriptionId = 0;
    _user = null;
    _isAuthenticated = false;

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    constructor() {
        Log.setLogger(console);
    }

    async isAuthenticated() {
        const user = await this.getUser();

        const obj = await this.getUserObject();

        if (user) {
            if (obj?.expired === true) {
                try {
                    const res = await this.signIn();

                    if (res.status === "success") {
                        return true;
                    } else {
                        return false;
                    }
                } catch {
                    return false;
                }
            } else {
                return true;
            }
        }
        return false;
    }

    async getUserObject() {
        if (this._user) {
            return this._user;
        }
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user;
    }

    async getUser() {
        // debugger;
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.profile;
    }

    async getOrganizationId() {
        const user = await this.userManager.getUser();
        return user && user.profile?.OrgId;
    }

    async getAccessToken() {
        await this.ensureUserManagerInitialized();
        const user = await this.userManager.getUser();
        return user && user.access_token;
    }

    // Sign-in is attempted silently using a hidden iframe (silent-renew.html).
    // This succeeds whenever an IdentityServer session cookie is present --
    // after a credential login, a SAML sign-in, or a previous session -- so
    // the SPA never performs a top-level redirect to /connect/authorize.
    // When it fails the caller is responsible for showing the interactive
    // login page (/auth/LoginUser). We deliberately do not fall back to
    // signinRedirect: the full-page round-trips it caused were the source of
    // the white-screen flashes during sign-in.
    async signIn(state) {
        await this.ensureUserManagerInitialized();

        try {
            const silentUser = await this.userManager.signinSilent(
                this.createArguments(),
            );
            this.updateState(silentUser);
            return this.success(state);
        } catch (silentError) {
            console.log("Silent authentication error: ", silentError);
            clearLocalStoragePreservingSamlLoginOption();
            this.userManager.removeUser();
            this.userManager.clearStaleState();
            return this.error(silentError?.message ?? "Silent sign-in failed");
        }
    }

    async completeSignIn(url) {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.signinRedirectCallback();
            this.updateState(user);
            return this.success(user && user.state);
        } catch (error) {
            console.log("There was an error signing in: ", error);

            return this.error(
                "There was an error signing in. name: " +
                    error?.name +
                    " msg: " +
                    error?.message +
                    " " +
                    JSON.stringify(error),
            );
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state) {
        await this.ensureUserManagerInitialized();
        try {
            if (this._popUpDisabled) {
                throw new Error(
                    "Popup disabled. Change 'AuthorizeService.js:AuthorizeService._popupDisabled' to false to enable it.",
                );
            }

            await this.userManager.signoutPopup(this.createArguments());
            this.updateState(undefined);
            return this.success(state);
        } catch (popupSignOutError) {
            console.log("Popup signout error: ", popupSignOutError);
            try {
                await this.userManager.signoutRedirect(
                    this.createArguments(state),
                );
                return this.redirect();
            } catch (redirectSignOutError) {
                console.log("Redirect signout error: ", redirectSignOutError);
                return this.error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url) {
        await this.ensureUserManagerInitialized();
        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);
            return this.success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return this.error(error);
        }
    }

    updateState(user) {
        this._user = user;
        this._isAuthenticated = !!this._user;
        this.notifySubscribers();
    }

    subscribe(callback) {
        this._callbacks.push({
            callback,
            subscription: this._nextSubscriptionId++,
        });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) =>
                element.subscription === subscriptionId
                    ? { found: true, index }
                    : { found: false },
            )
            .filter((element) => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(
                `Found an invalid number of subscriptions ${subscriptionIndex.length}`,
            );
        }

        this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    createArguments(state) {
        return { useReplaceToNavigate: true, data: state };
    }

    error(message) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    _initLock = false;
    async ensureUserManagerInitialized() {
        if (this.userManager !== undefined) {
            return;
        }
        if (this._initLock) {
            while (this._initLock === true) {
                await delay(1000);
            }
            this._initLock = false;
            return;
        }
        this._initLock = true;

        let response = await fetch(
            ApplicationPaths.ApiAuthorizationClientConfigurationUrl,
        );
        if (!response.ok) {
            throw new Error(`Could not load settings for '${ApplicationName}'`);
        }

        let settings = await response.json();

        // eslint-disable-next-line no-restricted-globals
        settings.silent_redirect_uri = `${location.protocol}//${location.host}/silent-renew.html`;

        // Preserve the oidc-client callback format instead of relying on the v3 default.
        settings.response_mode = "query";
        settings.automaticSilentRenew = true;
        settings.includeIdTokenInSilentRenew = true;
        settings.userStore = new WebStorageStateStore({
            prefix: ApplicationName,
        });
        settings.monitorSession = false;
        settings.checkSessionIntervalInSeconds = 2;
        // console.log(settings);
        settings.loadUserInfo = true;
        
        this.userManager = new UserManager(settings);

        this._initLock = false;
        // debugger;

        // handling moved to AuthStore
        // this.userManager.events.addUserSignedOut(async () => {
        //     // debugger;
        //     await this.userManager.removeUser();
        //     this.updateState(undefined);
        //     console.log("user signed out");
        // });
        // this.userManager.events.addAccessTokenExpiring(function(arg){
        //     console.log("token expiring...");
        //     // debugger;
        // });
    }

    static get instance() {
        return authService;
    }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: "redirect",
    Success: "success",
    Fail: "fail",
};
